'use client';
import { jsx as _jsx } from "react/jsx-runtime";
// eslint-disable-next-line no-restricted-imports -- Internal modal utility that intentionally wraps a Radix primitive.
import { FocusScope } from '@radix-ui/react-focus-scope';
import { useCallback } from 'react';
/**
 * FocusTrap constrains keyboard focus within its children using Radix UI's FocusScope.
 *
 * On mount, focus is placed on the FocusScope container itself rather than on the
 * first interactive element. This prevents side effects like tooltips triggering
 * on icon buttons. The user can then Tab to navigate to the first focusable element.
 *
 * Built on `@radix-ui/react-focus-scope`, the same primitive used by Radix Dialog.
 *
 **/
export function FocusTrap({ children, active = true, restoreFocus = true, }) {
    const handleMountAutoFocus = useCallback((event) => {
        // Prevent Radix from focusing the first tabbable element, which can
        // trigger side effects (e.g. tooltips on icon buttons). Instead, focus
        // the FocusScope container itself — focus stays inside the trap, but no
        // interactive element is activated until the user presses Tab.
        event.preventDefault();
        if (event.target instanceof HTMLElement) {
            event.target.focus();
        }
    }, []);
    const handleUnmountAutoFocus = useCallback((event) => {
        const shouldRestoreFocus = typeof restoreFocus === 'function' ? restoreFocus() : restoreFocus;
        if (!shouldRestoreFocus) {
            // Prevent Radix from restoring focus on unmount — focus should stay where it is
            event.preventDefault();
        }
    }, [restoreFocus]);
    return (_jsx(FocusScope, { trapped: false, loop: active, onMountAutoFocus: handleMountAutoFocus, onUnmountAutoFocus: handleUnmountAutoFocus, tabIndex: -1, 
        // remove focus ring from FocusScope container itself, since it serves only as a focus boundary and should not be interactable
        className: "outline-hidden", children: children }));
}
//# sourceMappingURL=FocusTrap.js.map